import {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { useBjForm } from '@hooks/useBjForm';


// Interface
// ************************************
interface UseApplicantsBulkOperationsProps {
  queryParams: object,
  formGroupName: string,
  list?: unknown[],
}


/**
 * @description Helper hook for bulk operations on applicants.
 *
 * Params:
 * - **queryParams** - object - The page query params object. We want to deselect all checkboxes when the filters change.
 * - **formGroupName** - string - The form group name.
 * - **list** - unknown, optional - The list of data. Default undefined. Needed to update the form elements when a new page is fetched.
 *
 * Return:
 * - **record** - RecordFunction - The record function from useBjForm.
 * - **handleStep** - HandleStepFunction - The handleStep function from useBjForm.
 * - **formRef** - React.RefObject<HTMLFormElement> - The form ref.
 * - **selectAll** - boolean | undefined - The select all state.
 * - **setSelectAll** - React.Dispatch<React.SetStateAction<boolean | undefined>> - The set select all state.
 * - **selectedCount** - number - The selected count state.
 * - **setSelectedCount** - React.Dispatch<React.SetStateAction<number>> - The set selected count state.
 * - **getSelectedApplicants** - () => string[] - The get selected applicants function. Pass function to bulk operations component.
 * - **hasFiltersApplied** - boolean - The has filters applied state.
 *
 * @example
 * const {
 *  record,
 *  handleStep,
 *  formRef,
 *  selectAll,
 *  setSelectAll,
 *  getSelectedApplicants,
 * } = useApplicantsBulkOperations({
 *  queryParams,
 *  formGroupName: 'applicantIds',
 * });
 *
 * // Inside render
 * <BulkOperations
 *   selectAll={selectAll}
 *   setSelectAll={setSelectAll}
 *   getSelectedApplicants={getSelectedApplicants}
 *   ...otherProps
 * />
 *
 * // Infinite list
 * <form noValidate onSubmit={handleStep} ref={formRef}>
 *   <TalentList
 *     applicant={{
 *       record,
 *       name: groupName,
 *       setSelectAll,
 *     }}
 *     ...otherProps
 *   />
 * </form>
 */
export const useApplicantsBulkOperations = (props: UseApplicantsBulkOperationsProps) => {
  // Destructure props
  const {
    queryParams,
    formGroupName,
    list,
  } = props;

  // State for checkboxes in bulk operations; we're using the 'undefined' state to handle the select all and then uncheck some
  // of the checkboxes, without triggering the check/uncheck all.
  // ***********************************************
  const [selectAll, setSelectAll] = useState<boolean | undefined>(undefined);

  // State for counting the number of actual selected checkboxes
  // ***********************************************
  const [selectedCount, setSelectedCount] = useState<number>(0);
  const [selectedApplicants, setSelectedApplicants] = useState<string[]>([]);

  // Check if filters are applied; If so, we need to build the list by slugs
  // ***********************************************
  const [hasFiltersApplied, setHasFiltersApplied] = useState<boolean>(false);
  const [selectByColumn, setSelectByColumn] = useState<{ status: string; statusFilter: string[] }[]>([]);

  // Form for bulk operations
  // ***********************************************
  const {
    record,
    handleStep,
    selectAllCheckboxes,
    getFormData,
    updateFormElements,
    selectGroupOfCheckboxes,
  } = useBjForm();

  const formRef = useRef<HTMLFormElement>(null);
  const getFormDataRef = useRef(getFormData);
  const updateFormElementsRef = useRef(updateFormElements);
  const selectAllCheckboxesRef = useRef(selectAllCheckboxes);
  const selectGroupOfCheckboxesRef = useRef(selectGroupOfCheckboxes);
  const lastSyncedListRef = useRef<unknown[] | undefined>(undefined);

  const areSelectedApplicantsEqual = (current: string[], next: string[]) => (
    current.length === next.length && current.every((slug, index) => slug === next[index])
  );

  useEffect(() => {
    getFormDataRef.current = getFormData;
    updateFormElementsRef.current = updateFormElements;
    selectAllCheckboxesRef.current = selectAllCheckboxes;
    selectGroupOfCheckboxesRef.current = selectGroupOfCheckboxes;
  }, [getFormData, updateFormElements, selectAllCheckboxes, selectGroupOfCheckboxes]);

  // Get selected applicants; pass function to bulk operations component
  // ***********************************************
  const getSelectedApplicants = useCallback(() => {
    const formData = getFormDataRef.current(formRef);
    const selectedApplicantsValue = formData?.[formGroupName as keyof typeof formData] as string[] || [];
    setSelectedCount((current) => (
      current === selectedApplicantsValue.length ? current : selectedApplicantsValue.length
    ));
    setSelectedApplicants((current) => (
      areSelectedApplicantsEqual(current, selectedApplicantsValue) ? current : selectedApplicantsValue
    ));
    return selectedApplicantsValue;
  }, [formGroupName]);

  const toggleColumnCheckboxes = useCallback(
    (columnType: string, checked: boolean, statusFilter: string[] = []) => {
      setSelectByColumn((prev) => {
        if (checked) {
          // Add column if not already present
          if (!prev.some((col) => col.status === columnType)) {
            return [...prev, { status: columnType, statusFilter }];
          }
          return prev;
        }
        // Remove column
        return prev.filter((col) => col.status !== columnType);
      });

      selectGroupOfCheckboxesRef.current(
        checked,
        [{ status: columnType, statusFilter }],
      );

      getSelectedApplicants();
    },
    [getSelectedApplicants],
  );


  const removeColumnFromSelectByColumn = useCallback((columnType: string) => {
    setSelectByColumn((prev) => prev.filter((col) => col.status !== columnType));
  }, []);

  const clearColumnCheckboxes = useCallback((columnType: string) => {
    if (!columnType) return;

    setSelectAll(undefined);
    setSelectByColumn((prev) => prev.filter((col) => col.status !== columnType));
    selectGroupOfCheckboxesRef.current(false, [{ status: columnType, statusFilter: [] }]);
    getSelectedApplicants();
  }, [getSelectedApplicants]);


  // Effects
  // ***********************************************
  // Handle bulk select/deselect
  useEffect(() => {
    if (selectAll !== undefined) {
      selectAllCheckboxesRef.current(selectAll, [formGroupName]);
      getSelectedApplicants();
    }
  }, [selectAll, formGroupName, getSelectedApplicants]);

  // Update form elements when a new page is fetched. With this, if select all is checked, and we fetch a new page,
  // the checkboxes will be checked automatically.
  useEffect(() => {
    if (list === undefined || lastSyncedListRef.current === list) return;

    lastSyncedListRef.current = list;
    updateFormElementsRef.current();
    getSelectedApplicants();
  }, [getSelectedApplicants, list]);

  // Deselect all checkboxes when the filters change. This is useful when the user selects some checkboxes,
  // then changes the filters. We want to deselect all checkboxes in this case. Also set the hasFiltersApplied state.
  useEffect(() => {
    const paramIsStatus = queryParams && Object.keys(queryParams).length === 1 && queryParams?.['status' as keyof typeof queryParams];
    setHasFiltersApplied(queryParams ? Object.keys(queryParams).length > 0 && !paramIsStatus : false);
    setSelectAll(false);
  }, [queryParams]);


  // Return
  // ***********************************************
  return {
    record,
    handleStep,
    formRef,
    toggleColumnCheckboxes,
    selectAll,
    setSelectAll,
    selectedCount,
    selectedApplicants,
    setSelectedCount,
    getSelectedApplicants,
    hasFiltersApplied,
    selectByColumn,
    removeColumnFromSelectByColumn,
    clearColumnCheckboxes,
  };
};
